import withCalendar, {Calendar, WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import {SearchInput} from "@FRAMEWORK/ui/desktop/input";
import {SelectBox, SelectLabel, SelectOptions, SelectWrap} from "@FRAMEWORK/ui/desktop/select/";
import SpriteImage from "@PUBLIC/P/images/ico/ico_spr.png";
import useContentsHoliday from "@QUERIES/essential/contents/useContentsHoliday";
import dayjs, {Dayjs} from "dayjs";
import {useEffect, useId, useState} from "react";
import styled, {css} from "styled-components";

interface DayProps {
	isActive?: boolean;
	CheckedDay?: boolean;
	today?: boolean;
	holiday?: boolean;
	types?: "basic" | "filter";
	rev?: "can" | "wait" | "end";
}

const Select = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	height: 100%;
	align-items: center;

	input {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 100%;
		padding: 7px 23px 7px 10px;
		background-color: transparent;
		box-sizing: border-box;
		cursor: pointer;

		+ label {
			position: absolute;
			top: 50%;
			right: 10px;
			display: block;
			width: 18px;
			height: 20px;
			background-image: url(${SpriteImage});
			background-size: 400px 400px;
			background-position: -20px 0;
			transform: translateY(-50%);
		}
	}
`;

const CalendarWrap = styled.div<{types?: "basic" | "filter"}>`
	${props => {
		if (props.types === "filter") {
			return css`
				position: absolute;
				left: 0;
				top: 36px;
				z-index: 10;
				border: 1px solid var(--emp-color);
				overflow: hidden;
			`;
		}
		if (props.types === "basic") {
			return css`
				width: 245px;
				margin: 0 auto;
			`;
		}
	}}
`;

const CalendarBox = styled.div<DayProps>`
	width: 245px;
	background-color: #fff;
	${props =>
		props.types === "basic" &&
		css`
			table {
				th {
					height: 35px;
					background-color: #fff;
					text-align: center;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
				td {
					height: 35px;
					padding: 0;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
			}
		`}
`;

const CalendarMonth = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	height: 50px;
	justify-content: center;
	em {
		font-size: 22px;
		font-weight: bold;
		color: var(--emp-color);
		line-height: 1;
	}
	label {
		font-size: var(--medium-size);
		font-weight: normal;
	}
	ul {
		top: 50px;
		left: 50%;
		width: 245px;
		max-height: 155px;
		transform: translateX(-50%);
		> li {
			justify-content: center;
			padding: 0;
		}
	}
`;

const MonthButton = styled.button<{types?: "prev" | "next"}>`
	position: absolute;
	top: 23px;
	width: 8px;
	height: 8px;
	border-left: 1px solid var(--emp-color);
	border-bottom: 1px solid var(--emp-color);
	background-color: transparent;

	&:disabled {
		border-color: var(--bl-color);
	}

	${props =>
		props.types === "prev"
			? `
                left: 10px;
                transform: rotate(45deg);
            `
			: `
                right: 10px;
                transform: rotate(-135deg);
            `}
`;

const TableCalendar = styled.table`
	width: 245px;
	table-layout: fixed;
`;

const Month = styled.th<DayProps>`
	height: 35px;
	padding: 10px;
	box-sizing: border-box;
	border-top: 1px solid var(--line-color);
	font-size: var(--basic-size);
	color: var(--emp-color);
	text-align: center;
	&:first-child {
		color: #ff5c46;
	}
	&:last-child {
		color: #4567ad;
	}
	${props => {
		if (props.types === "filter") {
			return css`
				font-size: var(--basic-size);
			`;
		}
		if (props.types === "basic") {
			return css`
				font-size: 13px;
			`;
		}
	}}
`;

const ProductDays = styled.td<DayProps>`
	position: relative;
	box-sizing: border-box;
	height: 35px;
	color: ${props => (props.isActive ? "var(--emp-color)" : "#aaa")};
	vertical-align: top;
	text-align: left;
	cursor: ${props => (props.isActive ? "pointer" : "initial")};

	${props => {
		if (props.types === "filter") {
			return css`
				> span {
					font-size: var(--basic-size);
				}
				&:hover {
					> span {
						${props.isActive && `border-color: var(--emp-color)`};
					}
				}
				&:first-child {
					> span {
						color: ${props.isActive ? "var(--red-color)" : "#ffc8c3"};
					}
				}
				&:last-child {
					> span {
						color: ${props.isActive ? "var(--blue-color)" : "#b5c2de"};
					}
				}
			`;
		}
		if (props.types === "basic") {
			return css`
				&:first-child {
					> span {
						color: ${props.rev ? "var(--red-color)" : "#ffc8c3"};
					}
				}
				&:last-child {
					> span {
						color: ${props.rev ? "var(--blue-color)" : "#b5c2de"};
					}
				}
				> span {
					font-size: 13px;
				}
				${props.rev
					? css`
							&:hover {
								cursor: pointer;
								> span {
									border-color: var(--emp-color);
								}
							}
						`
					: ""}
			`;
		}
	}}
	> span {
		border: 1px solid transparent;
		border-color: ${props => (props.CheckedDay ? "var(--emp-color)" : "transparent")};
		box-sizing: border-box;
		${props => {
			if (props.types === "basic" && props.rev === "can") {
				return css`
					background-color: var(--yellow-color);
					color: var(--emp-color);
					${props.isActive && `border-color: var(--emp-color)`}
				`;
			}
			if (props.types === "basic" && props.rev === "end") {
				return css`
					background-color: var(--line-color);
					color: var(--emp-color);
				`;
			}
			if (props.types === "basic" && props.rev === "wait") {
				return css`
					background-color: #a4bdf0;
					color: var(--emp-color);
				`;
			}
		}}
	}
`;

const Day = styled.span<DayProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	z-index: 1;
	color: ${props => (props.holiday ? "var(--red-color)" : "")};
	&:before {
		position: absolute;
		top: 0;
		left: 50%;
		display: ${props => (props.today ? "block" : "none")};
		width: 5px;
		height: 5px;
		border-radius: 100%;
		background-color: var(--emp-color);
		transform: translateX(-50%);
		content: "";
	}
`;

const ResetBlock = styled.div`
	display: flex;
	align-items: center;
	height: 35px;
	line-height: 35px;
	padding-left: 10px;
	border-top: 1px solid var(--line-color);
	background: var(--disabled-color);
	text-align: left;
`;

const ResetButton = styled.button`
	display: flex;
	align-items: center;
	gap: 10px;
	background-color: transparent;
	font-size: 14px;
	color: #222;
	&:before {
		display: block;
		width: 13px;
		height: 14px;
		background: url(${SpriteImage}) no-repeat;
		background-size: 400px 400px;
		background-position: -178px 0;
		content: "";
	}
`;

const LocalFilterCalendar = withCalendar((props: Calendar, ref: WithCalendarRef) => {
	const types = "filter";
	const today = dayjs();
	const {
		date,
		isNext,
		isPrevious,
		rangeMonth,
		dayList,
		month,
		clearDate,
		dateChange,
		monthChange,
		nextMonth,
		prevMonth,
	} = props;

	const [showCalendar, setShowCalendar] = useState(false);
	const [showMonth, setShowMonth] = useState(false);
	const id = useId();

	const holidayFetch = useContentsHoliday(dayjs(month).format("YYYY"), {staleTime: 1000 * 60 * 5});
	const holiday = holidayFetch.data?.body ?? [];

	useEffect(() => {
		if (!showCalendar) return;
		const event = e => {
			if (document.body.contains(e.target)) {
				!e.target.closest(`#${CSS.escape(id)}`) && setShowCalendar(false);
				!e.target.closest(`#selectWrap-${CSS.escape(id)}`) && setShowMonth(false);
				e.target.closest(`#options-${CSS.escape(id)}`) && setShowMonth(false);
			}
		};
		setTimeout(() => window.addEventListener("click", event));
		return () => {
			window.removeEventListener("click", event);
		};
	}, [showCalendar]);

	// filter data 동기화
	const {getFilter} = useFilterStore();
	const outStartDateValue = getFilter()?.filter(i => i.key === "outStartDateList")[0]?.value || "";

	return (
		<div>
			<Select>
				<SearchInput
					icon="calendar"
					type="text"
					readOnly
					onClick={() => {
						setShowCalendar(!showCalendar);
						setShowMonth(false);
					}}
					value={outStartDateValue?.length === 8 ? dayjs(outStartDateValue).format("YYYY.MM.DD") : ""}
				/>
				{showCalendar && (
					<CalendarWrap id={id} types={types}>
						<CalendarBox types={types}>
							<CalendarMonth>
								<SelectWrap width={90} id={`selectWrap-${id}`}>
									<SelectBox types="calendar" onClick={() => setShowMonth(!showMonth)}>
										<SelectLabel isActive>{month.format("YYYY.MM")}</SelectLabel>
									</SelectBox>
									{showMonth && (
										<SelectOptions id={`options-${id}`}>
											{rangeMonth?.map((day: Dayjs, index) => (
												<li
													value={day.format("YYYYMMDD")}
													key={index}
													onClick={e => {
														monthChange(String(e.currentTarget.value));
													}}
												>
													{day.format("YYYY.MM")}
												</li>
											))}
										</SelectOptions>
									)}
								</SelectWrap>
								{isPrevious && (
									<MonthButton types="prev" onClick={prevMonth}>
										<span>이전달</span>
									</MonthButton>
								)}
								{isNext && (
									<MonthButton types="next" onClick={nextMonth}>
										<span>다음달</span>
									</MonthButton>
								)}
							</CalendarMonth>
							<TableCalendar>
								<thead>
									<tr>
										<Month types={types}>일</Month>
										<Month types={types}>월</Month>
										<Month types={types}>화</Month>
										<Month types={types}>수</Month>
										<Month types={types}>목</Month>
										<Month types={types}>금</Month>
										<Month types={types}>토</Month>
									</tr>
								</thead>
								<tbody>
									{dayList?.map((week, index) => (
										<tr key={index}>
											{week.map((day, weekIdx) => {
												const dayFormat = day?.format("YYYYMMDD");
												const isHoliday = !!holiday.find(v => v.calDt === dayFormat);
												return (
													<ProductDays
														types={types}
														isActive={!(day < today.add(-1, "day"))}
														onClick={() => {
															if (!(day < today.add(-1, "day"))) {
																dateChange(day);
																setShowCalendar(false);
															}
														}}
														key={weekIdx}
														CheckedDay={date && date.isSame(day)}
													>
														<Day types={types} today={day && today.isSame(day, "day")} holiday={isHoliday}>
															{day && day.format("DD")}
														</Day>
													</ProductDays>
												);
											})}
										</tr>
									))}
								</tbody>
							</TableCalendar>
							<ResetBlock>
								<ResetButton onClick={clearDate}>날짜 재선택</ResetButton>
							</ResetBlock>
						</CalendarBox>
					</CalendarWrap>
				)}
			</Select>
		</div>
	);
});

export default LocalFilterCalendar;
